// APIAccess (/api) — public landing for the AHP data layer.
//
// Visual language matches /home-beta and /data-methodology: patriotic palette
// (navy / red / gold / cream), Instrument Serif headlines, Instrument Sans
// body, soft eyebrow chips, alternating section backgrounds. Stats below are
// verified counts from the production DB on 2026-05-13.
//
// The functional payload (endpoint catalog, license tiers, application form
// → api_access_requests table) is unchanged from the previous design — only
// the visual layer was reskinned.

import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, Code2, Database,
  Globe, Shield, ShieldCheck, Terminal, Zap, ChevronDown, ChevronUp,
} from "lucide-react";

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";
const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";

// ─── Theme tokens — same shape as HomeBeta + DataMethodology ──────────────────
function useThemeTokens() {
  const { isDark } = useTheme();
  return useMemo(() => {
    if (isDark) {
      return {
        bg: "#0a1628", bgAlt: "#050e1a", bgInverse: "#FAF9F6",
        text: "#FAF9F6", textOnInverse: "#1B1B1B",
        textMuted: "rgba(250,249,246,0.6)", textMutedOnInverse: "rgba(27,27,27,0.62)",
        gold: "#C5A55A", red: "#B22234", blue: "#5B9BD5", navy: "#002868",
        cardBg: "#0d1e35", cardBorder: "rgba(250,249,246,0.10)",
        divider: "rgba(250,249,246,0.10)", chipBg: "rgba(197,165,90,0.10)",
        codeBg: "rgba(250,249,246,0.06)",
      };
    }
    return {
      bg: "#FAF9F6", bgAlt: "#ffffff", bgInverse: "#0a1628",
      text: "#1B1B1B", textOnInverse: "#FAF9F6",
      textMuted: "rgba(27,27,27,0.62)", textMutedOnInverse: "rgba(250,249,246,0.65)",
      gold: "#C5A55A", red: "#B22234", blue: "#002868", navy: "#002868",
      cardBg: "#ffffff", cardBorder: "rgba(27,27,27,0.08)",
      divider: "rgba(27,27,27,0.08)", chipBg: "rgba(197,165,90,0.10)",
      codeBg: "rgba(27,27,27,0.06)",
    };
  }, [isDark]);
}

// ─── Stats (verified against the live DB on 2026-05-13) ───────────────────────
const STATS: { value: string; label: string; sub?: string }[] = [
  { value: "9,500+",  label: "US hospitals indexed", sub: "every state · public chargemasters" },
  { value: "562M",    label: "Chargemaster pricing lines",  sub: "raw rows ingested + normalized" },
  { value: "841M",    label: "Per-hospital analytics rows", sub: "every code × every hospital × markup" },
  { value: "2020 →",  label: "Year-over-year history",     sub: "tracked as hospitals re-publish" },
];

// ─── Endpoint catalog — mirrors public/openapi.json (the LIVE /api/v1 surface).
// The previous catalog documented /api/cpt-codes-style routes that were never
// deployed — all 11 returned 404 (launch audit 2026-07-12, finding 70). Keep
// this list in sync with openapi.json paths; /api/docs renders the same spec.
const ENDPOINTS = [
  { tag: "CODES",  full: "List codes by type",                       route: "GET /api/v1/codes/{type}",         desc: "Browse or search CPT, HCPCS, MS-DRG, and revenue codes with consumer-friendly descriptions and national average pricing. Paginated.", example: "/api/v1/codes/cpt?q=mri&limit=25",              note: "Code descriptions are our interpretations for consumer understanding, not official AMA/CMS/NUBC-licensed text." },
  { tag: "CODE",   full: "Single-code pricing buckets",              route: "GET /api/v1/codes/{type}/{code}",  desc: "National pricing for one billing code — average, median, min/max, and hospital count, broken down by inpatient/outpatient setting and billing unit.", example: "/api/v1/codes/cpt/99213?setting=outpatient", note: "The same numbers that power the consumer code pages." },
  { tag: "INTL",   full: "Cross-country code pricing",               route: "GET /api/v1/international/codes/{type}/{code}", desc: "What the same procedure costs in 32 other countries' published tariffs, with the U.S.-multiple for each.", example: "/api/v1/international/codes/cpt/70450?sort=multiplier", note: "Country tariffs from published national fee schedules." },
  { tag: "INTL",   full: "Countries with pricing data",              route: "GET /api/v1/international/countries", desc: "The country catalog: healthcare system type, currency, and overall U.S.-vs-country cost multiplier.", example: "/api/v1/international/countries", note: "" },
];

const HOSPITAL_ENDPOINTS = [
  { route: "GET /api/v1/scenarios",                       desc: "List or search 11,000+ real-cost medical scenarios with category, family, and cost-range filters. Paginated.", example: "?q=knee%20replacement&sort=cost&limit=25" },
  { route: "GET /api/v1/scenarios/{id}",                  desc: "One scenario with its full procedure roster — every billing code, phase, quantity, and the blended national cost.", example: "/api/v1/scenarios/{uuid}" },
  { route: "GET /api/v1/scenarios/by-code/{type}/{code}", desc: "Reverse lookup: every scenario that includes a given billing code.", example: "/api/v1/scenarios/by-code/drg/470" },
  { route: "GET /api/v1/scenarios/families",              desc: "Scenario families (severity ladders of the same condition) and their variants.", example: "?q=childbirth" },
  { route: "GET /api/v1/scenarios/{id}/international",    desc: "Cross-country total-cost comparison for a full scenario.", example: "?sort=savings" },
];

const AVAILABLE_ENDPOINTS = [
  "hospitals", "cpt_codes", "drg_codes", "revenue_codes",
  "drug_codes", "supply_codes", "hospital_analytics", "pricing_averages", "hospital_rankings",
];

// Free-tier and commercial-tier feature lists.
const FREE_TIER = [
  "Academic research",
  "Personal projects",
  "Educational purposes",
  "Non-profit organizations",
  "Journalism and media",
];
const COMMERCIAL_TIER = [
  "Commercial applications",
  "For-profit products & services",
  "Healthcare software vendors",
  "Insurance companies",
  "Consulting services",
];

export default function APIAccess() {
  const brand = useBrand();
  const { toast } = useToast();
  const T = useThemeTokens();
  const { isDark } = useTheme();

  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    full_name: "", email: "", company_name: "", job_title: "", phone: "",
    use_case: "", use_case_description: "", organization_type: "",
    estimated_requests_per_month: "", interested_endpoints: [] as string[],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("api_access_requests").insert({
        full_name: formData.full_name, email: formData.email,
        company_name: formData.company_name || null, job_title: formData.job_title || null,
        phone: formData.phone || null, use_case: formData.use_case,
        use_case_description: formData.use_case_description,
        organization_type: formData.organization_type || null,
        estimated_requests_per_month: formData.estimated_requests_per_month
          ? parseInt(formData.estimated_requests_per_month) : null,
        interested_endpoints: formData.interested_endpoints, status: "pending",
      });
      if (error) throw error;
      toast({ title: "Application submitted", description: "We'll review and reply within 48 hours.", duration: 5000 });
      setFormData({ full_name: "", email: "", company_name: "", job_title: "", phone: "", use_case: "", use_case_description: "", organization_type: "", estimated_requests_per_month: "", interested_endpoints: [] });
      setShowForm(false);
    } catch {
      toast({ title: "Submission failed", description: "There was an error submitting your request. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleEndpoint = (ep: string) => setFormData((prev) => ({
    ...prev,
    interested_endpoints: prev.interested_endpoints.includes(ep)
      ? prev.interested_endpoints.filter((e) => e !== ep)
      : [...prev.interested_endpoints, ep],
  }));

  return (
    <div style={{ background: T.bg, color: T.text, fontFamily: sans, paddingBottom: 96 }}>
      <Helmet>
        <title>API access — Hospital pricing data | {brand.name}</title>
        <meta name="description" content="Free REST API for US hospital pricing data. 9,500+ hospitals, 555+ million chargemaster lines, year-over-year history. Built for journalism, research, and consumer tools." />
        <meta name="keywords" content="hospital price API, chargemaster API, healthcare price data API, CPT pricing API, MS-DRG pricing API, hospital pricing database API" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/api`} />
        <meta property="og:title" content="API access — Hospital pricing data" />
        <meta property="og:description" content="Free REST API for US hospital pricing data. 9,500+ hospitals indexed, 562M chargemaster lines, year-over-year history." />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/api`} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="API access — Hospital pricing data" />
        <meta name="twitter:description" content="Free REST API for US hospital pricing data. 9,500+ hospitals indexed, 562M chargemaster lines." />
        <meta name="twitter:image" content={brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
      </Helmet>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6 text-center" style={{ maxWidth: 1100, paddingTop: 96, paddingBottom: 72 }}>
          <p style={eyebrowStyle(T)}>Public utility · open data, open methodology</p>
          <h1 style={h1Style(T)}>
            An <em style={{ fontStyle: "italic", color: T.red }}>API</em>. An <em style={{ fontStyle: "italic", color: T.red }}>MCP</em>. A <em style={{ fontStyle: "italic", color: T.red }}>CDN</em>.
          </h1>
          <p style={subStyle(T)}>
            The blended national chargemaster index for the United States — every CPT, HCPCS, MS-DRG, revenue, drug, and supply code,
            broken down by inpatient / outpatient setting, with year-over-year history as hospitals re-publish. Free for journalism,
            research, and consumer tools.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            {/* Primary CTA → /api/access (MVP waitlist form, writes to
                api_interest_signups). The richer in-page application form
                further down still works but is no longer the first option;
                Master will retire it once the waitlist captures enough
                signal to decide on tiers. */}
            <Link to="/api/access" style={primaryCtaStyle(T)}>
              Request API access <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/api/docs" style={secondaryCtaStyle(T)}>
              Read the docs <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            {/* The REST API is served by Pages Functions on the main domain
                (openapi.json servers) — api.americanhealthprices.com is the
                Supabase/Kong gateway where /api/v1/* 401s. */}
            <a href="https://americanhospitalprices.com/api/v1/scenarios" target="_blank" rel="noreferrer" style={secondaryCtaStyle(T)}>
              americanhospitalprices.com/api/v1 <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* ── Stats strip ─────────────────────────────────────────────────── */}
      <section style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}` }}>
        <div
          className="mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-10"
          style={{ maxWidth: 1100, paddingTop: 48, paddingBottom: 48 }}
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <div style={{ fontFamily: serif, fontSize: 44, lineHeight: 1, color: T.navy, fontWeight: 400 }}>
                {s.value}
              </div>
              <div style={{ fontFamily: sans, fontSize: 13, color: T.text, marginTop: 8, fontWeight: 600 }}>
                {s.label}
              </div>
              {s.sub && (
                <div style={{ fontFamily: sans, fontSize: 12, color: T.textMuted, marginTop: 4 }}>
                  {s.sub}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── What's in here ─────────────────────────────────────────────── */}
      <section style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1100, paddingTop: 72, paddingBottom: 32 }}>
          <p style={eyebrowStyle(T)}>What's in here</p>
          <h2 style={h2Style(T)}>Three surfaces. Same data.</h2>
          <div className="grid lg:grid-cols-3 gap-6 mt-10">
            <Card T={T} icon={<Code2 className="w-5 h-5" />} title="REST API" sub="americanhospitalprices.com/api/v1" body="Standard JSON, no key required for non-commercial use. Same endpoints power the consumer site." />
            <Card T={T} icon={<Terminal className="w-5 h-5" />} title="MCP server" sub="mcp.americanhospitalprices.com" body="Native MCP tools for Claude, ChatGPT desktop, and other agents. Ask in plain English; receive structured pricing rows." />
            <Card T={T} icon={<Database className="w-5 h-5" />} title="CDN mirror" sub="static.americanhealthprices.com" body="Static JSON snapshots of the rankings, scenarios, and national averages. Versioned per data refresh. Free egress." />
          </div>
        </div>
      </section>

      {/* ── Licensing ───────────────────────────────────────────────────── */}
      <section style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1100, paddingTop: 72, paddingBottom: 72 }}>
          <p style={eyebrowStyle(T)}>Licensing</p>
          <h2 style={h2Style(T)}>Free for the public good.</h2>
          <p style={{ ...subStyle(T), marginTop: 16, maxWidth: 720 }}>
            Non-commercial use is free with no API key. Commercial integrations need a license — write to us about your use case and
            we'll route it through review within 48 hours.
          </p>

          <div className="grid lg:grid-cols-2 gap-6 mt-10">
            {/* Free */}
            <div style={tierCardStyle(T, "green")}>
              <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
                <CheckCircle2 className="w-5 h-5" style={{ color: "#10B981" }} />
                <span style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, color: T.text, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  Free · No API key required
                </span>
              </div>
              {FREE_TIER.map((item) => (
                <div key={item} style={tierRowStyle(T)}>
                  <span style={{ color: "#10B981", fontSize: 10 }}>●</span>{item}
                </div>
              ))}
            </div>

            {/* Commercial */}
            <div style={tierCardStyle(T, "gold")}>
              <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
                <Shield className="w-5 h-5" style={{ color: T.gold }} />
                <span style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, color: T.text, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  Commercial license
                </span>
              </div>
              {COMMERCIAL_TIER.map((item) => (
                <div key={item} style={tierRowStyle(T)}>
                  <span style={{ color: T.gold, fontSize: 10 }}>●</span>{item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Medical code endpoints ──────────────────────────────────────── */}
      <section style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1100, paddingTop: 72, paddingBottom: 72 }}>
          <p style={eyebrowStyle(T)}>Medical code endpoints</p>
          <h2 style={h2Style(T)}>Every code system, one shape.</h2>
          <p style={{ ...subStyle(T), marginTop: 16, maxWidth: 720 }}>
            JSON responses with consumer-friendly descriptions, per-setting averages (inpatient / outpatient / all-settings), and
            year-over-year price points where hospitals have re-published the underlying code.
          </p>

          <div className="mt-10" style={{ display: "grid", gap: 10 }}>
            {ENDPOINTS.map((ep) => {
              const isOpen = expandedEndpoint === ep.tag;
              return (
                <div key={ep.tag} style={endpointCardStyle(T, isOpen)}>
                  <button
                    type="button"
                    onClick={() => setExpandedEndpoint(isOpen ? null : ep.tag)}
                    style={endpointHeaderBtnStyle()}
                  >
                    <span style={endpointTagStyle(T)}>{ep.tag}</span>
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <div style={{ fontFamily: mono, fontSize: 13, color: T.navy, fontWeight: 600 }}>
                        {ep.route}
                      </div>
                      <div style={{ fontFamily: sans, fontSize: 13, color: T.textMuted, marginTop: 2 }}>
                        {ep.full}
                      </div>
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4" style={{ color: T.textMuted }} /> : <ChevronDown className="w-4 h-4" style={{ color: T.textMuted }} />}
                  </button>
                  {isOpen && (
                    <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${T.divider}` }}>
                      <p style={{ fontFamily: sans, fontSize: 14, color: T.text, margin: "14px 0 8px", lineHeight: 1.6 }}>
                        {ep.desc}
                      </p>
                      <p style={{ fontFamily: sans, fontSize: 12, color: T.textMuted, fontStyle: "italic", marginBottom: 12, lineHeight: 1.5 }}>
                        Note: {ep.note}
                      </p>
                      <code style={codeBlockStyle(T)}>{ep.example}</code>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Scenario endpoints ──────────────────────────────────────────── */}
      <section style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1100, paddingTop: 72, paddingBottom: 72 }}>
          <p style={eyebrowStyle(T)}>Scenarios</p>
          <h2 style={h2Style(T)}>The same data behind the consumer site.</h2>
          <p style={{ ...subStyle(T), marginTop: 16, maxWidth: 720 }}>
            Full medical scenarios — every billing code, phase, and quantity, priced against the blended national
            chargemaster index that powers the consumer scenario pages.
          </p>

          <div className="mt-10" style={{ display: "grid", gap: 12 }}>
            {HOSPITAL_ENDPOINTS.map((ep) => (
              <div key={ep.route} style={hospitalCardStyle(T)}>
                <div style={{ fontFamily: mono, fontSize: 14, color: T.navy, fontWeight: 600, marginBottom: 6 }}>
                  {ep.route}
                </div>
                <p style={{ fontFamily: sans, fontSize: 13, color: T.text, margin: "0 0 12px", lineHeight: 1.6 }}>
                  {ep.desc}
                </p>
                <code style={codeBlockStyle(T)}>{ep.example}</code>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Apply CTA / form ────────────────────────────────────────────── */}
      <section style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" id="apply-form" style={{ maxWidth: 1100, paddingTop: 72, paddingBottom: 72 }}>
          {!showForm ? (
            <div style={{ textAlign: "center" }}>
              <p style={eyebrowStyle(T)}>Get access</p>
              <h2 style={h2Style(T)}>Ready to build with it?</h2>
              <p style={{ ...subStyle(T), marginTop: 16, maxWidth: 620, marginInline: "auto" }}>
                Tell us about your use case. Non-commercial keys are usually live within 48 hours; commercial inquiries get a
                response with terms in the same window.
              </p>
              <button type="button" onClick={() => setShowForm(true)} style={{ ...primaryCtaStyle(T), marginTop: 28 }}>
                Apply for API access <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div style={formCardStyle(T)}>
              <div style={formHeaderStyle(T)}>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontFamily: serif, fontSize: 24, fontWeight: 400, color: T.textOnInverse, margin: 0 }}>
                    API access application
                  </h2>
                  <p style={{ fontFamily: sans, fontSize: 13, color: T.textMutedOnInverse, margin: "4px 0 0" }}>
                    Tell us about your use case — we'll review within 48 hours.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} style={{ padding: 28 }}>
                <FormSection T={T} title="Contact information">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField T={T} label="Full name *" id="full_name" required value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} />
                    <FormField T={T} label="Email address *" id="email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <FormField T={T} label="Company / organization" id="company_name" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} />
                    <FormField T={T} label="Job title / role" id="job_title" value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} />
                  </div>
                  <div className="mt-4">
                    <FormField T={T} label="Phone number" id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                </FormSection>

                <FormSection T={T} title="Use case">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="use_case" style={fieldLabelStyle(T)}>Intended use *</Label>
                      <Select required value={formData.use_case} onValueChange={(v) => setFormData({ ...formData, use_case: v })}>
                        <SelectTrigger style={selectTriggerStyle(T)}><SelectValue placeholder="Select use case" /></SelectTrigger>
                        <SelectContent>
                          {[["research","Academic Research"],["nonprofit","Non-Profit"],["personal","Personal Project"],["education","Educational"],["journalism","Journalism / Media"],["commercial","Commercial Use"],["other","Other"]].map(([v,l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="org_type" style={fieldLabelStyle(T)}>Organization type</Label>
                      <Select value={formData.organization_type} onValueChange={(v) => setFormData({ ...formData, organization_type: v })}>
                        <SelectTrigger style={selectTriggerStyle(T)}><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          {[["healthcare","Healthcare provider"],["insurance","Insurance"],["research","Research institution"],["tech","Technology company"],["media","Media / News"],["government","Government agency"],["other","Other"]].map(([v,l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label htmlFor="use_case_description" style={fieldLabelStyle(T)}>Describe your use case *</Label>
                    <Textarea
                      id="use_case_description"
                      required
                      rows={4}
                      value={formData.use_case_description}
                      onChange={(e) => setFormData({ ...formData, use_case_description: e.target.value })}
                      placeholder="Tell us how you plan to use the API and what problem you're trying to solve."
                      style={{ ...selectTriggerStyle(T), height: "auto", padding: "12px 14px", resize: "vertical" }}
                    />
                  </div>
                </FormSection>

                <FormSection T={T} title="Technical">
                  <FormField T={T} label="Estimated requests per month" id="estimated_requests" type="number" value={formData.estimated_requests_per_month} onChange={(e) => setFormData({ ...formData, estimated_requests_per_month: e.target.value })} placeholder="e.g., 10,000" />
                  <div className="mt-5">
                    <div style={fieldLabelStyle(T)}>Interested endpoints</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {AVAILABLE_ENDPOINTS.map((ep) => {
                        const checked = formData.interested_endpoints.includes(ep);
                        return (
                          <div
                            key={ep}
                            onClick={() => toggleEndpoint(ep)}
                            style={{
                              display: "flex", alignItems: "center", gap: 8,
                              padding: "8px 12px", cursor: "pointer",
                              border: `1px solid ${checked ? T.navy : T.cardBorder}`,
                              background: checked ? T.chipBg : "transparent",
                              borderRadius: 8,
                            }}
                          >
                            <Checkbox id={ep} checked={checked} onCheckedChange={() => toggleEndpoint(ep)} style={{ borderColor: T.navy }} />
                            <label htmlFor={ep} style={{ fontFamily: mono, fontSize: 12, color: T.text, cursor: "pointer", userSelect: "none" }}>{ep}</label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </FormSection>

                <div className="flex flex-wrap gap-3 pt-2">
                  <button type="submit" disabled={isSubmitting} style={{ ...primaryCtaStyle(T), flex: "1 1 220px", opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? "not-allowed" : "pointer" }}>
                    {isSubmitting ? "Submitting…" : "Submit application"}
                    {!isSubmitting && <ArrowRight className="w-4 h-4" />}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} disabled={isSubmitting} style={secondaryCtaStyle(T)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <section>
        <div className="mx-auto px-6 text-center" style={{ maxWidth: 720, paddingTop: 56, paddingBottom: 8 }}>
          <div className="flex items-center gap-2 justify-center" style={{ color: T.gold, marginBottom: 8 }}>
            <div style={{ height: 1, width: 32, background: T.gold, opacity: 0.5 }} />
            <ShieldCheck className="w-3.5 h-3.5" />
            <div style={{ height: 1, width: 32, background: T.gold, opacity: 0.5 }} />
          </div>
          <p style={{ fontFamily: sans, fontSize: 12, color: T.textMuted }}>
            {brand.name} · Open data for every American
          </p>
        </div>
      </section>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function Card({ T, icon, title, sub, body }: { T: ReturnType<typeof useThemeTokens>; icon: React.ReactNode; title: string; sub: string; body: string }) {
  return (
    <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 24 }}>
      <div className="flex items-center gap-2" style={{ color: T.gold, marginBottom: 10 }}>
        {icon}
        <span style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em" }}>
          {title}
        </span>
      </div>
      <div style={{ fontFamily: mono, fontSize: 12, color: T.text, marginBottom: 12 }}>{sub}</div>
      <p style={{ fontFamily: sans, fontSize: 14, color: T.textMuted, lineHeight: 1.55, margin: 0 }}>{body}</p>
    </div>
  );
}

function FormSection({ T, title, children }: { T: ReturnType<typeof useThemeTokens>; title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.gold,
        textTransform: "uppercase", letterSpacing: "0.12em",
        paddingBottom: 10, borderBottom: `1px solid ${T.divider}`, marginBottom: 18,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function FormField({ T, label, id, type = "text", required = false, value, onChange, placeholder }: {
  T: ReturnType<typeof useThemeTokens>;
  label: string; id: string; type?: string; required?: boolean;
  value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; placeholder?: string;
}) {
  return (
    <div>
      <Label htmlFor={id} style={fieldLabelStyle(T)}>{label}</Label>
      <Input id={id} type={type} required={required} value={value} onChange={onChange} placeholder={placeholder} style={selectTriggerStyle(T)} />
    </div>
  );
}

// ─── Style helpers ───────────────────────────────────────────────────────────

function eyebrowStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    fontFamily: sans, fontSize: 12, fontWeight: 600,
    letterSpacing: "2px", textTransform: "uppercase",
    color: T.gold, marginBottom: 14,
  };
}

function h1Style(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    fontFamily: serif, fontSize: "clamp(38px, 6.5vw, 76px)", fontWeight: 400,
    lineHeight: 1.05, letterSpacing: "-1.5px", color: T.text, marginBottom: 22,
  };
}

function h2Style(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    fontFamily: serif, fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 400,
    lineHeight: 1.1, letterSpacing: "-1px", color: T.text, margin: 0,
  };
}

function subStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    fontFamily: sans, fontSize: "clamp(15px, 1.6vw, 18px)", lineHeight: 1.6,
    color: T.textMuted, maxWidth: 720, marginInline: "auto",
  };
}

function primaryCtaStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: T.red, color: "#FAF9F6", border: 0,
    padding: "14px 26px", borderRadius: 6,
    fontFamily: sans, fontSize: 14, fontWeight: 600,
    letterSpacing: "0.02em", cursor: "pointer",
    boxShadow: "0 8px 24px rgba(178,34,52,0.25)",
  };
}

function secondaryCtaStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "transparent", color: T.text,
    border: `1px solid ${T.cardBorder}`,
    padding: "13px 22px", borderRadius: 6,
    fontFamily: sans, fontSize: 14, fontWeight: 500,
    textDecoration: "none", cursor: "pointer",
  };
}

function tierCardStyle(T: ReturnType<typeof useThemeTokens>, accent: "green" | "gold"): React.CSSProperties {
  return {
    background: T.cardBg, border: `1px solid ${T.cardBorder}`,
    borderRadius: 12, padding: 24,
    borderLeft: `4px solid ${accent === "green" ? "#10B981" : T.gold}`,
  };
}

function tierRowStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    fontFamily: sans, fontSize: 14, color: T.text,
    padding: "5px 0", display: "flex", alignItems: "center", gap: 10,
  };
}

function endpointCardStyle(T: ReturnType<typeof useThemeTokens>, isOpen: boolean): React.CSSProperties {
  return {
    background: T.cardBg, border: `1px solid ${T.cardBorder}`,
    borderRadius: 10, overflow: "hidden",
    boxShadow: isOpen ? "0 4px 18px rgba(0,40,104,0.08)" : "0 1px 4px rgba(0,40,104,0.03)",
    transition: "box-shadow 0.2s",
  };
}

function endpointHeaderBtnStyle(): React.CSSProperties {
  return {
    width: "100%", display: "flex", alignItems: "center", gap: 14,
    padding: "16px 20px", background: "none", border: 0, cursor: "pointer",
  };
}

function endpointTagStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    background: T.navy, color: "#FAF9F6",
    fontFamily: mono, fontWeight: 700, fontSize: 12,
    padding: "4px 10px", letterSpacing: "0.05em",
    flexShrink: 0, minWidth: 76, textAlign: "center",
    borderRadius: 4,
  };
}

function hospitalCardStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    background: T.cardBg, border: `1px solid ${T.cardBorder}`,
    borderRadius: 10, padding: 20,
  };
}

function codeBlockStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    display: "inline-block",
    background: T.codeBg, border: `1px solid ${T.divider}`,
    borderLeft: `3px solid ${T.gold}`,
    padding: "8px 12px", borderRadius: 4,
    fontFamily: mono, fontSize: 12, color: T.text,
  };
}

function formCardStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    background: T.cardBg, border: `1px solid ${T.cardBorder}`,
    borderRadius: 14, overflow: "hidden",
    boxShadow: "0 12px 40px rgba(0,40,104,0.10)",
  };
}

function formHeaderStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    background: T.bgInverse, padding: "22px 28px",
    display: "flex", alignItems: "center", gap: 12,
  };
}

function fieldLabelStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    display: "block", marginBottom: 6,
    fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.text,
    textTransform: "uppercase", letterSpacing: "0.1em",
  };
}

function selectTriggerStyle(T: ReturnType<typeof useThemeTokens>): React.CSSProperties {
  return {
    border: `1px solid ${T.cardBorder}`, borderRadius: 8,
    height: 42, padding: "0 14px",
    fontFamily: sans, fontSize: 14, color: T.text,
    background: T.cardBg,
  };
}
